import React, {FC, LegacyRef, useEffect, useMemo} from 'react';
import styles from './ProjectPlayer.module.css';
import {FormControl, InputLabel, MenuItem, Select, SelectChangeEvent} from "@mui/material";
import VideoPlayer from "../VideoPlayer/VideoPlayer";
import VideoPlayerControls from "../VideoPlayerControls/VideoPlayerControls";
import FrameStatsCard from "../FrameStatsCard/FrameStatsCard";
import {useAppDispatch, useAppSelector} from "../../hooks";
import GiocondaBrandTopBar from "../GiocondaBrandTopBar/GiocondaBrandTopBar";
import {Navigate, useParams} from "react-router-dom";
import {assert} from "../../assert";
import {activeVideoActions} from "../../state-slices/ActiveVideo";
import ToolsCard from "../ToolsCard/ToolsCard";
import AssetList from "../AssetList/AssetList";
import {ResizableBox} from 'react-resizable';
import ScreenshotDialog from "../ScreenshotDialog/ScreenshotDialog";
import {debounce} from "lodash";
import {Resolution} from "../../state-slices/state-management/constants/Resolution";

interface DashboardProps {
}

const ProjectPlayer: FC<DashboardProps> = () => {
    const dispatch = useAppDispatch();
    const isBuffering = useAppSelector(state => state.activeVideoSlice.videoIsBuffering);
    const shouldShowRuler = useAppSelector(state => state.activeVideoSlice.shouldShowRuler);
    const isFullScreen = useAppSelector(state => state.activeVideoSlice.projectState.showFullScreen);
    const showFullScreenControls = useAppSelector(state => state.activeVideoSlice.projectState.showFullScreenControlBar);
    const currentProject = useAppSelector(state => state.activeVideoSlice.projectState.currentProject)
    const currentRun = useAppSelector(state => state.activeVideoSlice.projectState.selectedRun)
    const currentClip = useAppSelector(state => state.activeVideoSlice.projectState.selectedClip)
    const currentResolution = useAppSelector(state => state.activeVideoSlice.projectState.selectedResolution)
    const availableProjects = useAppSelector(state => state.activeVideoSlice.projectState.availableProjects)
    const showAssetList = useAppSelector(state => state.activeVideoSlice.projectState.isAssetPanelOpen)
    const {projectSafeName} = useParams();

    useEffect(() => {
        function onFullscreenChange() {
            const isFullScreen = Boolean(document.fullscreenElement);
            let actionToMatchFullScreenState =
                isFullScreen
                    ? activeVideoActions.showFullScreen()
                    : activeVideoActions.exitFullScreen();
            dispatch(actionToMatchFullScreenState);
        }

        document.addEventListener('fullscreenchange', onFullscreenChange);
        return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
    }, [dispatch]);

    useEffect(() => {
        let playPause = (keyEvent: KeyboardEvent) => {
            console.log(keyEvent.key);
            if(keyEvent.target !== window.document.body)
                return;
            if(keyEvent.key === ' ' && !shouldShowRuler)
                dispatch(activeVideoActions.playOrStop());
            if(keyEvent.code === 'ArrowLeft')
                dispatch(activeVideoActions.frameChangeRelative(-1));
            if(keyEvent.code === 'ArrowRight')
                dispatch(activeVideoActions.frameChangeRelative(1))
        };
        document.addEventListener('keyup', playPause);
        return () => document.removeEventListener('keyup', playPause);
    }, [dispatch, shouldShowRuler]);

    const mouseInactivityDebounce = useMemo<() => void>(() => {
        return debounce(() => {
            dispatch(activeVideoActions.mouseInactive());
        }, 2000);
    }, [dispatch]);

    assert(projectSafeName, 'project safe name param missing');

    if(!currentProject)
        return <Navigate to={`/projects/play/${projectSafeName}/init`}/>;

    if(!currentResolution)
        return <Navigate to="/" replace={true}/>;

    assert(currentClip, 'no current clip');
    assert(currentRun, 'no current run');

    const projectOptions = availableProjects.map((p) =>
        (<MenuItem key={p.name} value={p.s3Key}>{p.displayName || p.name}</MenuItem>))

    function getProjectOptions() {
        if (currentProject === null) {
            return {
                runOptions: [],
                clipOptions: [],
                resolutionOptions: []
            }
        }

        const runOptions = currentProject?.runs
            .map((p) => (<MenuItem key={p.safeName} value={p.safeName}>{p.name}</MenuItem>))

        const clipNameMapping = new Map()
        clipNameMapping.set('RAW', 'Forward');
        clipNameMapping.set('REV', 'Reverse');
        clipNameMapping.set('QUR', 'Down');

        const clipOptions = currentRun?.availableClips
            .map((p) => (<MenuItem key={p.name} value={p.name}>{clipNameMapping.get(p.name) || p.name}</MenuItem>))
        const resolutionOptions = currentClip?.resolutions.map((p) => (
            <MenuItem key={p.resolution} value={p.resolution}>{p.resolution}</MenuItem>))

        return {
            runOptions,
            clipOptions,
            resolutionOptions
        }
    }

    const {runOptions, clipOptions, resolutionOptions} = getProjectOptions();

    const projectChanged = (e: SelectChangeEvent<string>) => {
        let selectedProjectName = e.target.value;
        dispatch(activeVideoActions.selectProject(selectedProjectName))
    }

    function runChanged(e: SelectChangeEvent<string>) {
        dispatch(activeVideoActions.selectRun(e.target.value));
    }

    function clipChanged(e: SelectChangeEvent<string>) {
        dispatch(activeVideoActions.selectClip(e.target.value));
    }

    function resolutionChanged(e: SelectChangeEvent<string>) {
        dispatch(activeVideoActions.seekCurrentTimeOnNextVideoLoad());
        dispatch(activeVideoActions.selectResolution(e.target.value as Resolution));
    }

    function mouseActivity() {
        if(!isFullScreen)
            return;
        dispatch(activeVideoActions.mouseActivity());
        mouseInactivityDebounce();
    }

    function setShowAssetList(isOpen: boolean) {
        dispatch(activeVideoActions.setAssetPanelState(isOpen))
    }

    function ResizeHandle(ref: LegacyRef<any>) {
        return (
            <div data-id="asset-resize-handle"
                onDoubleClick={() => setShowAssetList(false)}
                ref={ref}
                style={{
                    display: "flex",
                    justifyContent: "center",
                    height: 20,
                    width: 20,
                    margin: 2,
                    userSelect: "none",
                    cursor: "ew-resize"
                }}
            >
                ||
            </div>
        );
    }

    return <div className={styles.Dashboard} onMouseMove={mouseActivity}>
        {!isFullScreen && <GiocondaBrandTopBar>
            <div className={styles.NavControls}>
                <div className={styles.TopDropdown}>
                    <FormControl style={{minWidth: 120}}>
                        <InputLabel id="project-label">Project</InputLabel>
                        <Select
                            data-id="project-selector"
                            labelId="project-label"
                            id="project-selector"
                            value={currentProject?.s3Key ?? ''}
                            label="Project"
                            onChange={projectChanged}>
                            {projectOptions}
                        </Select>
                    </FormControl>
                </div>
                {<div className={styles.ProjectControls}>
                    <div className={styles.TopDropdown}>
                        <FormControl style={{minWidth: 220}}>
                            <InputLabel id="run-label">Run</InputLabel>
                            <Select
                                data-id="run-selector"
                                labelId="run-label"
                                id="run-selector"
                                value={currentRun.safeName}
                                label="Run"
                                onChange={runChanged}>
                                {runOptions}
                            </Select>
                        </FormControl>
                    </div>
                    <div>
                        <FormControl style={{minWidth: 80}}>
                            <InputLabel id="clip-label">View</InputLabel>
                            <Select
                                data-id="clip-selector"
                                labelId="clip-label"
                                id="clip-selector"
                                value={currentClip?.name ?? null}
                                label="View"
                                onChange={clipChanged}>
                                {clipOptions}
                            </Select>
                        </FormControl>
                    </div>
                    <span className={styles.Spacer}/>
                    {isBuffering && <span>Buffering</span>}
                    <div className={styles.TopDropdown}>
                        <FormControl>
                            <InputLabel id="quality-label">Quality</InputLabel>
                            <Select
                                data-id="quality-selector"
                                labelId="quality-label"
                                id="quality-selector"
                                value={currentResolution?.resolution ?? null}
                                label="Quality"
                                onChange={resolutionChanged}>
                                {resolutionOptions}
                            </Select>
                        </FormControl>
                    </div>
                </div>}
            </div>
        </GiocondaBrandTopBar>}
        <div className={styles.Middle}>
            {showAssetList && <ResizableBox className={styles.LeftBar} width={300} axis={'x'}
                           resizeHandles={['e']}
                           handle={(h, ref) => ResizeHandle(ref)}
                           minConstraints={[200, 100]} maxConstraints={[1000, 300]}>
                    <AssetList/>
            </ResizableBox>}

            <div className={styles.VideoPlayerContainer} style={isFullScreen ? {padding: 0} : {}}>
                <VideoPlayer/>
            </div>

            {!isFullScreen && <div className={styles.RightBar}>
                <div className={styles.CardContainer}>
                    <FrameStatsCard/>
                </div>
                <div className={styles.CardContainer}>
                    <ToolsCard/>
                </div>
            </div> }
        </div>
        {!isFullScreen && <div className={styles.BottomControlBar}>
            <VideoPlayerControls/>
        </div>}
        { isFullScreen && showFullScreenControls && <div className={styles.FullScreenControlBar}>
            <VideoPlayerControls/>
        </div> }
        <ScreenshotDialog></ScreenshotDialog>
    </div>
};

export default ProjectPlayer;
